import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate } from '~/types/dates'
import type { PhaseId } from '~/types/ids'
import type { Kilograms } from '~/types/units'
import { toKilograms, unwrapNumber } from '~/types/units'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { type ContextTag, isContextTag } from './contextTags'

/**
 * A daily weight entry, keyed by its calendar date (D-027).
 *
 * One entry per day is structural rather than a validation rule. Re-entering on
 * an existing date overwrites the value and records correction metadata;
 * deletion is a separate explicit action (D-035).
 */
export interface WeightEntryRecord extends RecordEnvelope {
  readonly date: CalendarDate
  readonly weightKg: Kilograms
  readonly note?: string
  readonly contextTags: readonly ContextTag[]
  /** The phase active when the entry was recorded, for grouping. */
  readonly phaseId?: PhaseId
  /** Set when this entry replaced an earlier value for the same date (D-035). */
  readonly correctedFromKg?: Kilograms
  readonly correctedAt?: IsoTimestamp
}

export interface WeightEntryInput {
  readonly date: string
  readonly weightKg: number
  readonly note?: string
  readonly contextTags?: readonly string[]
}

/* Bounds catch transposed digits and unit mistakes, not unusual bodies. They
   are intentionally wide. */
const MIN_WEIGHT_KG = 20
const MAX_WEIGHT_KG = 400

export function validateWeightEntry(input: WeightEntryInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (toCalendarDate(input.date) === undefined) {
    issues.push(issue('date', 'Date must be a real date in YYYY-MM-DD form.'))
  }

  const weight = toKilograms(input.weightKg)
  if (weight === undefined) {
    issues.push(issue('weightKg', 'Weight must be a positive number in kilograms.'))
  } else {
    const value = unwrapNumber(weight)
    if (value < MIN_WEIGHT_KG || value > MAX_WEIGHT_KG) {
      issues.push(
        issue('weightKg', `Weight must be between ${MIN_WEIGHT_KG} and ${MAX_WEIGHT_KG} kg.`),
      )
    }
  }

  for (const tag of input.contextTags ?? []) {
    if (!isContextTag(tag)) {
      issues.push(issue('contextTags', `Unknown context tag: ${tag}.`))
    }
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]
